/**
 * A tmux session whose Claude is waiting for an answer — a permission dialog, a
 * question only a person can answer — is pinned to the topbar on its own, so it
 * is one click away from any view, and unpinned again once it is answered.
 *
 * Only a pin put there by this (`auto` on the pin) is taken away: one the reader
 * pinned stays. Unpinning an automatic pin is heard as "not now" — it is not put
 * back until that Claude has been answered and asks again. One on screen is not
 * pinned; it is if the reader leaves it still unanswered.
 *
 * Only a session in a repo of one of the reader's teams (lib/repoSettings.ts
 * `repoOnBoard`, as on Personal) pins itself: another team's Claude waiting is
 * that team's to answer. A session in no repo is nobody's team's, and pins as before.
 *
 * The tmux view feeds its own list in (`noteSessions`); on any other view
 * `useAutoPin()` takes the server's push (lib/sessions.ts), for an admin on a
 * board that offers the tmux view, after one read of its own.
 */
import { onBeforeUnmount, ref, watch } from "vue";
import { api } from "./api.js";
import { pushedSessions, sessionsGen } from "./sessions.js";
import { useBoard } from "../stores/board.js";
import { useViewport } from "../stores/viewport.js";
import { loadRepoInfos, repoInfos, repoInfosError, repoInfosLoaded, repoOnBoard } from "./repoSettings.js";
import type { Pin } from "@shared/settings.js";
import type { TmuxSession } from "@shared/tmux.js";

/** The sessions whose Claude is waiting, as last read: their pills are marked. */
export const waitingSessions = ref<ReadonlySet<string>>(new Set());

/** Sessions already pinned (or found pinned, or unpinned by the reader) since
 *  they last began to wait. Cleared as each stops waiting. */
const handled = new Set<string>();

/** Take a fresh session list: pin what began to wait, unpin what was answered. */
export function noteSessions(list: readonly TmuxSession[]): void {
  const viewport = useViewport();
  const all = list.filter((s) => s.claude?.state === "waiting");
  waitingSessions.value = new Set(all.map((s) => s.name));
  // Not the reader's teams': not pinned, and an automatic pin of one goes.
  const waiting = new Set(all.filter(ofMyTeams).map((s) => s.name));
  for (const name of handled) if (!waiting.has(name)) handled.delete(name);

  const onScreen = viewport.view === "tmux" ? viewport.tmuxShown?.session ?? null : null;
  let pins: Pin[] = viewport.pins.filter((p) => !(p.kind === "session" && p.auto && !waiting.has(p.name)));
  for (const s of list) {
    if (!waiting.has(s.name) || handled.has(s.name)) continue;
    if (pins.some((p) => p.kind === "session" && p.name === s.name)) {
      handled.add(s.name);
      continue;
    }
    if (s.name === onScreen) continue;
    pins = [...pins, { kind: "session", name: s.name, taskId: s.taskId ?? null, auto: true }];
    handled.add(s.name);
  }
  if (pins.length !== viewport.pins.length || pins.some((p, i) => p !== viewport.pins[i])) viewport.pins = pins;
}

/** Whether a session's repo is on one of the reader's teams' boards. A repo the
 *  list does not know yet (not read, or the bridge did not answer) is not. */
function ofMyTeams(s: TmuxSession): boolean {
  if (!s.repo) return true;
  const info = repoInfos.value.find((r) => r.name === s.repo);
  return !!info && repoOnBoard(info, null, useBoard().teams);
}

/** Follows the session list while the tmux view, which feeds its own in, is not
 *  on screen: one read as it starts (and on each reconnect), then the pushes. */
export function useAutoPin(): void {
  const board = useBoard();
  const viewport = useViewport();
  const on = (): boolean => board.loaded && board.tmuxAvailable && viewport.view !== "tmux";
  /** Bumped by each turn off: a read from before one is not taken. */
  let gen = 0;

  async function read(): Promise<void> {
    const mine = gen;
    try {
      const { sessions } = await api.get<{ sessions: TmuxSession[] }>("/api/tmux/sessions");
      // Which team each repo is in, read once; a failed read is tried again on the next.
      if (!repoInfosLoaded.value || (repoInfosError.value && !repoInfos.value.length)) await loadRepoInfos();
      // A push meanwhile is newer: this one is taken only if none came.
      if (mine === gen && on()) noteSessions(sessions);
    } catch {
      // The bridge is down, or the network: the pins stay as they are.
    }
  }

  watch(
    on,
    (now) => {
      gen++;
      if (now) void read();
    },
    { immediate: true },
  );
  watch(sessionsGen, () => {
    if (on()) void read();
  });
  watch(pushedSessions, (list) => {
    if (!list || !on()) return;
    gen++;
    if (!repoInfosLoaded.value) void loadRepoInfos();
    noteSessions(list);
  });

  onBeforeUnmount(() => void gen++);
}
